{
    "$schema": "https://cogo.xyz/ontology/v1/action-types",
    "version": "1.0.0",
    "description": "Canonical registry of all valid ActionFlow actions",
    "actions": {
        "setState": {
            "category": "data",
            "description": "Update a state variable. With operation:\"append\"/\"prepend\" adds value to an array target (the runtime-canonical way to add a list item); operation:\"removeWhere\"/\"updateWhere\" select rows with a `predicate` arrow-function string (NEVER a `where` key — the runtime reads params.predicate exclusively, action-flow-engine.ts:1647/:1656) and updateWhere additionally takes `update`; operation:\"upsert\" takes `match` + `onMatch`/`onNew`; without operation the value is replaced.",
            "requiredParams": [
                [
                    "target"
                ]
            ],
            "optionalParams": [
                "value",
                "operation",
                "predicate",
                "update",
                "match",
                "onMatch",
                "onNew"
            ],
            "tags": [
                "state",
                "update",
                "variable"
            ]
        },
        "setVariable": {
            "category": "data",
            "description": "Set a named variable",
            "requiredParams": [
                [
                    "name",
                    "key"
                ]
            ],
            "optionalParams": [
                "value"
            ],
            "tags": [
                "variable",
                "assign"
            ]
        },
        "compute": {
            "category": "data",
            "description": "Compute expression and store result",
            "requiredParams": [
                [
                    "target"
                ],
                [
                    "expression"
                ]
            ],
            "optionalParams": [],
            "tags": [
                "calculate",
                "expression"
            ]
        },
        "restApi": {
            "category": "data",
            "description": "Make REST API HTTP request. The response is written to the state path named by `params.resultTarget` — that is the ONE canonical result-key name (action-flow-engine.ts:1977 reads resultTarget ?? target ?? responseKey, plus a legacy resultKey that it re-prefixes with `_results.`). It is NOT `resultPath` and NOT the step-level `key`: restApi's executor returns void, so `_results.<step.name>` auto-capture never fires for it. Set `resultTarget: \"_results.<name>\"` for any downstream `#{_results.<name>...}` read.",
            "requiredParams": [
                [
                    "url"
                ]
            ],
            "optionalParams": [
                "method",
                "headers",
                "body",
                "credentials",
                "resultTarget",
                "timeout",
                "retryPolicy",
                "throwOnHttpError"
            ],
            "tags": [
                "http",
                "fetch",
                "api",
                "request"
            ]
        },
        "graphqlAction": {
            "category": "data",
            "description": "Execute GraphQL query or mutation",
            "requiredParams": [
                [
                    "url",
                    "endpoint"
                ],
                [
                    "query"
                ]
            ],
            "optionalParams": [
                "variables",
                "resultTarget"
            ],
            "tags": [
                "graphql",
                "query",
                "mutation"
            ]
        },
        "supabaseQuery": {
            "category": "data",
            "description": "Query Supabase table",
            "requiredParams": [
                [
                    "table"
                ]
            ],
            "optionalParams": [
                "method",
                "columns",
                "filters",
                "order",
                "range",
                "single",
                "count",
                "data",
                "resultTarget"
            ],
            "tags": [
                "supabase",
                "database",
                "query"
            ]
        },
        "supabaseRpc": {
            "category": "data",
            "description": "Call Supabase RPC function",
            "requiredParams": [
                [
                    "functionName"
                ]
            ],
            "optionalParams": [
                "args",
                "resultTarget"
            ],
            "tags": [
                "supabase",
                "rpc",
                "function"
            ]
        },
        "navigate": {
            "category": "navigation",
            "description": "Navigate to a route",
            "requiredParams": [],
            "optionalParams": [
                "route",
                "mode",
                "params"
            ],
            "runtimeRules": {
                "routeRequired": "unless mode is 'back' or 'forward'",
                "commonMistakes": {
                    "path": "use 'route' instead"
                }
            },
            "tags": [
                "route",
                "page",
                "go"
            ]
        },
        "goLink": {
            "category": "navigation",
            "description": "Open external URL",
            "requiredParams": [
                [
                    "url"
                ]
            ],
            "optionalParams": [
                "target"
            ],
            "tags": [
                "external",
                "link",
                "open"
            ]
        },
        "emitEvent": {
            "category": "event",
            "description": "Emit a named app event directly without wrapping one atomic action in an ActionFlow",
            "requiredParams": [
                [
                    "event"
                ]
            ],
            "optionalParams": [
                "payload"
            ],
            "tags": [
                "event",
                "emit",
                "direct"
            ]
        },
        "runMotion": {
            "category": "motion",
            "description": "Run a UI animation and await its completion before the next ActionFlow step",
            "requiredParams": [
                [
                    "target"
                ],
                [
                    "preset"
                ]
            ],
            "optionalParams": [
                "durationMs",
                "easing",
                "fill",
                "reducedMotion"
            ],
            "tags": [
                "motion",
                "animation",
                "completion-barrier",
                "ui"
            ]
        },
        "scrollToPosition": {
            "category": "navigation",
            "description": "Scroll to a specific x/y coordinate",
            "requiredParams": [],
            "optionalParams": [
                "x",
                "y",
                "smooth"
            ],
            "tags": [
                "scroll",
                "position",
                "top"
            ]
        },
        "showToast": {
            "category": "ui",
            "description": "Show toast notification",
            "requiredParams": [
                [
                    "message"
                ]
            ],
            "optionalParams": [
                "title",
                "description",
                "variant",
                "duration"
            ],
            "tags": [
                "notification",
                "toast",
                "alert"
            ]
        },
        "showPopup": {
            "category": "ui",
            "description": "Show popup dialog",
            "requiredParams": [
                [
                    "title"
                ],
                [
                    "message"
                ]
            ],
            "optionalParams": [
                "actions",
                "variant"
            ],
            "tags": [
                "dialog",
                "modal",
                "alert"
            ]
        },
        "confirmAction": {
            "category": "ui",
            "description": "Show a blocking confirmation dialog (e.g. before placeOrder). Halts the ActionFlow if user cancels; subsequent steps only run on confirm. Wave 4 R6 — cofo:Checkout AC4.3 review-step confirm gate.",
            "requiredParams": [
                [
                    "title"
                ],
                [
                    "message"
                ]
            ],
            "optionalParams": [
                "confirmLabel",
                "cancelLabel",
                "variant"
            ],
            "tags": [
                "dialog",
                "modal",
                "confirm",
                "gate",
                "checkout"
            ]
        },
        "showLoading": {
            "category": "ui",
            "description": "Show/hide loading overlay",
            "requiredParams": [],
            "optionalParams": [
                "show",
                "message"
            ],
            "tags": [
                "loading",
                "spinner"
            ]
        },
        "notification": {
            "category": "ui",
            "description": "System notification",
            "requiredParams": [
                [
                    "title"
                ]
            ],
            "optionalParams": [
                "body",
                "icon"
            ],
            "tags": [
                "push",
                "notification"
            ]
        },
        "callFlow": {
            "category": "flow",
            "description": "Invoke another flow (local or cross-block)",
            "requiredParams": [
                [
                    "flow",
                    "flowName"
                ]
            ],
            "optionalParams": [
                "params"
            ],
            "tags": [
                "delegate",
                "invoke",
                "reuse"
            ]
        },
        "delay": {
            "category": "flow",
            "description": "Wait for specified duration",
            "requiredParams": [
                [
                    "duration"
                ]
            ],
            "optionalParams": [],
            "tags": [
                "wait",
                "sleep",
                "timeout"
            ]
        },
        "conditionalDelay": {
            "category": "flow",
            "description": "Wait while condition is true",
            "requiredParams": [
                [
                    "condition"
                ],
                [
                    "duration",
                    "ms"
                ]
            ],
            "optionalParams": [],
            "tags": [
                "wait",
                "poll"
            ]
        },
        "stopFlow": {
            "category": "flow",
            "description": "Stop current flow execution",
            "requiredParams": [],
            "optionalParams": [],
            "tags": [
                "halt",
                "break",
                "return"
            ]
        },
        "batchAction": {
            "category": "flow",
            "description": "Execute multiple actions as a batch",
            "requiredParams": [
                [
                    "actions"
                ]
            ],
            "optionalParams": [],
            "tags": [
                "batch",
                "bulk"
            ]
        },
        "shareAction": {
            "category": "media",
            "description": "Share content via system share dialog",
            "requiredParams": [],
            "optionalParams": [
                "text",
                "url",
                "title"
            ],
            "tags": [
                "share",
                "social"
            ]
        },
        "downloadAction": {
            "category": "media",
            "description": "Download a file",
            "requiredParams": [
                [
                    "url"
                ]
            ],
            "optionalParams": [
                "filename"
            ],
            "tags": [
                "download",
                "file",
                "save"
            ]
        },
        "csvExport": {
            "category": "media",
            "description": "Export data as CSV file",
            "requiredParams": [
                [
                    "data"
                ]
            ],
            "optionalParams": [
                "filename",
                "headers"
            ],
            "tags": [
                "export",
                "csv",
                "spreadsheet"
            ]
        },
        "jsonExport": {
            "category": "media",
            "description": "Export data as JSON file download",
            "requiredParams": [
                [
                    "data"
                ]
            ],
            "optionalParams": [
                "filename"
            ],
            "tags": [
                "export",
                "json",
                "download"
            ]
        },
        "fileUpload": {
            "category": "media",
            "description": "Upload a file",
            "requiredParams": [],
            "optionalParams": [
                "accept",
                "maxSize",
                "resultTarget"
            ],
            "tags": [
                "upload",
                "file"
            ]
        },
        "mediaCapture": {
            "category": "media",
            "description": "Capture photo or video",
            "requiredParams": [],
            "optionalParams": [
                "type",
                "resultTarget"
            ],
            "tags": [
                "camera",
                "photo",
                "video"
            ]
        },
        "copyToClipboard": {
            "category": "utility",
            "description": "Copy text to clipboard",
            "requiredParams": [
                [
                    "text",
                    "value"
                ]
            ],
            "optionalParams": [],
            "tags": [
                "clipboard",
                "copy"
            ]
        },
        "clipboardRead": {
            "category": "utility",
            "description": "Read from clipboard",
            "requiredParams": [],
            "optionalParams": [
                "resultTarget"
            ],
            "tags": [
                "clipboard",
                "paste"
            ]
        },
        "storage": {
            "category": "utility",
            "description": "Local storage operations",
            "requiredParams": [
                [
                    "operation"
                ]
            ],
            "optionalParams": [
                "key",
                "value"
            ],
            "tags": [
                "localStorage",
                "persist"
            ]
        },
        "geolocation": {
            "category": "utility",
            "description": "Get device location",
            "requiredParams": [],
            "optionalParams": [
                "resultTarget"
            ],
            "tags": [
                "location",
                "gps"
            ]
        },
        "printAction": {
            "category": "utility",
            "description": "Print current page",
            "requiredParams": [],
            "optionalParams": [],
            "tags": [
                "print"
            ]
        },
        "debounceAction": {
            "category": "utility",
            "description": "Debounce action execution",
            "requiredParams": [
                [
                    "action"
                ]
            ],
            "optionalParams": [
                "delay"
            ],
            "tags": [
                "debounce",
                "delay"
            ]
        },
        "payment": {
            "category": "crypto",
            "description": "Process payment",
            "requiredParams": [],
            "optionalParams": [
                "amount",
                "currency",
                "provider"
            ],
            "tags": [
                "pay",
                "checkout"
            ]
        },
        "web3": {
            "category": "crypto",
            "description": "Web3 blockchain interaction",
            "requiredParams": [],
            "optionalParams": [
                "method",
                "params"
            ],
            "tags": [
                "blockchain",
                "web3"
            ]
        },
        "cryptoAction": {
            "category": "crypto",
            "description": "Cryptocurrency operation",
            "requiredParams": [],
            "optionalParams": [],
            "tags": [
                "crypto"
            ]
        },
        "websocket": {
            "category": "data",
            "description": "WebSocket connection management",
            "requiredParams": [
                [
                    "action"
                ]
            ],
            "optionalParams": [
                "url",
                "message"
            ],
            "tags": [
                "ws",
                "realtime",
                "socket"
            ]
        },
        "supabaseRealtime": {
            "category": "realtime",
            "description": "Subscribe to postgres changes, broadcast messages, and presence via Supabase Realtime channels",
            "requiredParams": [
                ["action"],
                ["channel"]
            ],
            "optionalParams": [
                "eventType",
                "event",
                "schema",
                "table",
                "filter",
                "triggerFlow",
                "payload",
                "state",
                "resultTarget"
            ],
            "tags": [
                "supabase",
                "realtime",
                "subscription",
                "presence",
                "broadcast"
            ]
        },
        "analyticsEvent": {
            "category": "utility",
            "description": "Send analytics event",
            "requiredParams": [],
            "optionalParams": [
                "event",
                "properties"
            ],
            "tags": [
                "analytics",
                "tracking"
            ]
        },
        "geminiAction": {
            "category": "utility",
            "description": "Call Gemini LLM API",
            "requiredParams": [],
            "optionalParams": [
                "prompt",
                "model"
            ],
            "tags": [
                "ai",
                "llm",
                "gemini"
            ]
        },
        "transactionAction": {
            "category": "data",
            "description": "Execute steps as atomic transaction",
            "requiredParams": [
                [
                    "steps"
                ]
            ],
            "optionalParams": [],
            "tags": [
                "transaction",
                "atomic"
            ]
        },
        "log": {
            "category": "utility",
            "description": "Log message to console",
            "requiredParams": [],
            "optionalParams": [
                "message",
                "level"
            ],
            "tags": [
                "debug",
                "console",
                "log"
            ]
        },
        "computeDateRange": {
            "category": "utility",
            "description": "Compute date range from preset",
            "requiredParams": [],
            "optionalParams": [
                "preset",
                "resultTarget"
            ],
            "tags": [
                "date",
                "range",
                "calendar"
            ]
        },
        "updateWhere": {
            "category": "state",
            "description": "Updates items in an array that match a condition. Selector is `predicate` (an arrow-function string) and the field dictionary is `updates` — NEVER `where`, and NOT `update` on this standalone action. The runtime dispatches it as `actionSetState({ ...params, operation: \"updateWhere\", update: params.updates })` (action-flow-engine.ts:309): the literal `update:` key OVERWRITES anything the caller passed as `update`, so only `updates` survives; and actionSetState reads `params.predicate` exclusively (:1656). Corpus agrees 70/87.",
            "requiredParams": [
                [
                    "target"
                ],
                [
                    "predicate"
                ],
                [
                    "updates"
                ]
            ],
            "optionalParams": [],
            "tags": [
                "array",
                "update",
                "filter",
                "conditional"
            ]
        },
        "upsertWhere": {
            "category": "state",
            "description": "Updates matching item or inserts new one if no match found",
            "requiredParams": [
                [
                    "target"
                ],
                [
                    "matchExpr"
                ]
            ],
            "optionalParams": [
                "onMatch",
                "onNew"
            ],
            "tags": [
                "array",
                "upsert",
                "cart"
            ]
        },
        "removeWhere": {
            "category": "state",
            "description": "Removes items from an array that match a condition. Selector is `predicate` (an arrow-function string) — NEVER `where`. Dispatched as `actionSetState({ ...params, operation: \"removeWhere\" })` (action-flow-engine.ts:308), which reads `params.predicate` exclusively (:1647). Corpus agrees 80/81.",
            "requiredParams": [
                [
                    "target"
                ],
                [
                    "predicate"
                ]
            ],
            "optionalParams": [],
            "tags": [
                "array",
                "remove",
                "filter",
                "conditional"
            ]
        },
        "groupBy": {
            "category": "data",
            "description": "Groups array items by a specified key",
            "requiredParams": [
                [
                    "target"
                ]
            ],
            "optionalParams": [
                "source",
                "key",
                "groupKey"
            ],
            "tags": [
                "array",
                "group",
                "aggregate"
            ]
        },
        "computeField": {
            "category": "data",
            "description": "Computes a derived field on array items",
            "requiredParams": [],
            "optionalParams": [
                "target",
                "source",
                "field",
                "expression"
            ],
            "tags": [
                "compute",
                "field",
                "transform"
            ]
        },
        "setTableCell": {
            "category": "data",
            "description": "Set a single cell value in a table array",
            "requiredParams": [
                [
                    "target"
                ],
                [
                    "rowIndex"
                ],
                [
                    "field"
                ]
            ],
            "optionalParams": [
                "value"
            ],
            "tags": [
                "table",
                "cell",
                "edit",
                "inline"
            ]
        },
        "addTableRow": {
            "category": "data",
            "description": "Add a new row to a table array",
            "requiredParams": [
                [
                    "target"
                ]
            ],
            "optionalParams": [
                "row",
                "position"
            ],
            "tags": [
                "table",
                "row",
                "add",
                "insert"
            ]
        },
        "deleteTableRow": {
            "category": "data",
            "description": "Delete a row from a table array by index",
            "requiredParams": [
                [
                    "target"
                ],
                [
                    "rowIndex"
                ]
            ],
            "optionalParams": [],
            "tags": [
                "table",
                "row",
                "delete",
                "remove"
            ]
        },
        "validate": {
            "category": "form",
            "description": "Validate params.target against params.rules (array of {type, value?, message?}) — the exact shapes actionValidate executes",
            "requiredParams": [
                [
                    "target"
                ],
                [
                    "rules"
                ]
            ],
            "optionalParams": [],
            "tags": [
                "form",
                "validation",
                "input"
            ]
        },
        "cfKv": {
            "category": "cloudflare",
            "description": "CF KV get/set/delete/list",
            "requiredParams": [
                ["action"]
            ],
            "optionalParams": [
                "key",
                "value",
                "ttl",
                "prefix",
                "resultTarget"
            ],
            "tags": ["kv", "edge", "storage"]
        },
        "cfR2": {
            "category": "cloudflare",
            "description": "CF R2 upload/download/list/delete",
            "requiredParams": [
                ["action"]
            ],
            "optionalParams": [
                "path",
                "file",
                "contentType",
                "prefix",
                "appId",
                "resultTarget"
            ],
            "tags": ["r2", "edge", "object-storage"]
        },
        "cfDurableObject": {
            "category": "cloudflare",
            "description": "CF Durable Object create/get/put/delete + alarm",
            "requiredParams": [
                ["action"]
            ],
            "optionalParams": [
                "objectId",
                "data",
                "alarmAt",
                "resultTarget"
            ],
            "tags": ["durable-object", "edge", "stateful"]
        },
        "cfAiGateway": {
            "category": "cloudflare",
            "description": "CF AI Gateway LLM request caching/rate-limit/logging",
            "requiredParams": [
                ["action"]
            ],
            "optionalParams": [
                "prompt",
                "model",
                "provider",
                "logData",
                "resultTarget"
            ],
            "tags": ["ai", "gateway", "edge"]
        },
        "googleGmail": {
            "category": "google",
            "description": "Send email via Gmail CF proxy",
            "requiredParams": [
                ["to"],
                ["subject"]
            ],
            "optionalParams": [
                "body",
                "cc",
                "bcc",
                "resultTarget"
            ],
            "tags": ["email", "gmail", "proxy"]
        },
        "googleFcm": {
            "category": "google",
            "description": "Send push notification via FCM CF proxy",
            "requiredParams": [
                ["title"],
                ["body"]
            ],
            "optionalParams": [
                "userId",
                "token",
                "data",
                "resultTarget"
            ],
            "tags": ["push", "fcm", "proxy"]
        },
        "geminiStream": {
            "category": "ai",
            "description": "Gemini streaming response with token-by-token output",
            "requiredParams": [
                ["prompt"]
            ],
            "optionalParams": [
                "model",
                "streamId",
                "resultTarget"
            ],
            "tags": ["gemini", "streaming", "llm"]
        },
        "aiCancel": {
            "category": "ai",
            "description": "Cancel active AI streaming request via AbortController",
            "requiredParams": [],
            "optionalParams": [
                "streamId"
            ],
            "tags": ["cancel", "abort", "streaming"]
        },
        "geminiVision": {
            "category": "ai",
            "description": "Gemini vision multimodal image analysis",
            "requiredParams": [
                ["prompt"]
            ],
            "optionalParams": [
                "imageUrl",
                "imageBase64",
                "images",
                "mimeType",
                "model",
                "apiKey",
                "resultTarget"
            ],
            "tags": ["gemini", "vision", "multimodal"]
        },
        "geminiFunctionCall": {
            "category": "ai",
            "description": "Gemini function calling agent pattern",
            "requiredParams": [
                ["prompt"]
            ],
            "optionalParams": [
                "model",
                "tools",
                "resultTarget"
            ],
            "tags": ["gemini", "function-call", "agent"]
        },
        "wallet.create": {
            "category": "wallet",
            "description": "Create new wallet",
            "requiredParams": [],
            "optionalParams": [
                "resultTarget"
            ],
            "tags": ["wallet", "create"]
        },
        "wallet.list": {
            "category": "wallet",
            "description": "List user wallets",
            "requiredParams": [],
            "optionalParams": [
                "network",
                "resultTarget"
            ],
            "tags": ["wallet", "list"]
        },
        "wallet.getBalance": {
            "category": "wallet",
            "description": "Get wallet balance",
            "requiredParams": [
                ["walletId"]
            ],
            "optionalParams": [
                "resultTarget"
            ],
            "tags": ["wallet", "balance"]
        },
        "wallet.syncBalance": {
            "category": "wallet",
            "description": "Sync wallet balance from chain",
            "requiredParams": [
                ["walletId"]
            ],
            "optionalParams": [
                "resultTarget"
            ],
            "tags": ["wallet", "sync"]
        },
        "wallet.send": {
            "category": "wallet",
            "description": "Send transaction",
            "requiredParams": [
                ["walletId"],
                ["to"],
                ["amount"]
            ],
            "optionalParams": [
                "resultTarget"
            ],
            "tags": ["wallet", "send", "transaction"]
        },
        "wallet.getTxStatus": {
            "category": "wallet",
            "description": "Get transaction status",
            "requiredParams": [
                ["requestId"]
            ],
            "optionalParams": [
                "resultTarget"
            ],
            "tags": ["wallet", "transaction", "status"]
        },
        "wallet.getTxHistory": {
            "category": "wallet",
            "description": "Get transaction history",
            "requiredParams": [
                ["walletId"]
            ],
            "optionalParams": [
                "network",
                "limit",
                "offset",
                "resultTarget"
            ],
            "tags": ["wallet", "transaction", "history"]
        },
        "wallet.estimateFee": {
            "category": "wallet",
            "description": "Estimate transaction fee",
            "requiredParams": [
                ["to"],
                ["amount"]
            ],
            "optionalParams": [
                "resultTarget"
            ],
            "tags": ["wallet", "fee", "estimate"]
        },
        "supabaseAuth": {
            "category": "auth",
            "description": "Supabase auth operations (signIn/signUp/signOut/etc.)",
            "requiredParams": [
                ["method"]
            ],
            "optionalParams": [
                "email",
                "password",
                "provider",
                "redirectTo",
                "metadata",
                "resultTarget"
            ],
            "tags": ["supabase", "auth"]
        },
        "supabaseStorage": {
            "category": "storage",
            "description": "Supabase storage upload/download/signed URL",
            "requiredParams": [
                ["method"]
            ],
            "optionalParams": [
                "bucket",
                "path",
                "file",
                "upsert",
                "contentType",
                "expiresIn",
                "resultTarget"
            ],
            "tags": ["supabase", "storage"]
        },
        "loadDatasource": {
            "category": "data",
            "description": "Load datasource from registry",
            "requiredParams": [
                ["datasourceId"]
            ],
            "optionalParams": [
                "resultTarget"
            ],
            "tags": ["datasource", "load"]
        },
        "queueAction": {
            "category": "utility",
            "description": "Queue an action for deferred execution (alias for queue)",
            "requiredParams": [
                ["action"]
            ],
            "optionalParams": [],
            "tags": ["queue", "defer"]
        },
        "retryAction": {
            "category": "utility",
            "description": "Retry an action on failure (alias for retry)",
            "requiredParams": [
                ["action"]
            ],
            "optionalParams": [
                "maxRetries",
                "delay"
            ],
            "tags": ["retry", "resilience"]
        },
        "throttleAction": {
            "category": "utility",
            "description": "Throttle action execution (alias for throttle)",
            "requiredParams": [
                ["action"]
            ],
            "optionalParams": [
                "interval"
            ],
            "tags": ["throttle", "rate-limit"]
        },
        "dispatchStoreAction": {
            "category": "data",
            "description": "Dispatch a named handler to the block's store — used in Studio blocks to trigger UI state mutations (open drawer, collapse, undo, redo, etc.)",
            "requiredParams": [["handler"]],
            "optionalParams": [],
            "tags": ["store", "dispatch", "handler", "studio", "state"]
        },
        "cogo.formula.dependencies": {
            "category": "data",
            "description": "Extract cell, range, and named-reference dependencies from a formula.",
            "requiredParams": [],
            "optionalParams": [
                "formula",
                "expression",
                "position",
                "cells",
                "sheets",
                "namedRefs"
            ],
            "tags": []
        },
        "cogo.formula.evaluate": {
            "category": "data",
            "description": "Evaluate an Excel-compatible formula through the Cogo runtime formula engine.",
            "requiredParams": [],
            "optionalParams": [
                "formula",
                "expression",
                "position",
                "cells",
                "sheets",
                "namedRefs",
                "allowReturnArray"
            ],
            "tags": []
        },
        "openAuthSession": {
            "category": "auth",
            "description": "Open an OAuth/SSO auth session in an in-app browser. On RN uses expo-web-browser openAuthSessionAsync. On web falls back to window.open popup.",
            "requiredParams": [
                ["url"]
            ],
            "optionalParams": [
                "redirectUrl",
                "resultTarget"
            ],
            "tags": []
        },
        "scrollToElement": {
            "category": "ui",
            "description": "Scroll the viewport to a target element by ID.",
            "requiredParams": [],
            "optionalParams": [
                "target",
                "sectionId",
                "behavior",
                "smooth"
            ],
            "tags": []
        },
        "sortByField": {
            "category": "data",
            "description": "Sort an array from a source state path by an object field and write the sorted result to a target path.",
            "requiredParams": [
                ["source"],
                ["target"],
                ["field"]
            ],
            "optionalParams": [
                "direction"
            ],
            "tags": []
        },
        "applyReportDesignTheme": {
            "category": "ui",
            "description": "Apply a report design-system theme by changing Cogo JSON token references or preview design state without changing report content.",
            "requiredParams": [
                ["themeId"]
            ],
            "optionalParams": [
                "surface",
                "accent",
                "tokenPatch",
                "target"
            ],
            "tags": ["report", "design-system", "tokenRef", "preview", "theme"]
        }
    },
    "runtimeCompatRules": {
        "paramNaming": {
            "error": "config -> params",
            "description": "Runtime expects 'params', not 'config'"
        },
        "navigateRoute": {
            "error": "path -> route",
            "description": "Navigate uses 'route', not 'path'"
        },
        "conditionalBranches": {
            "error": "thenSteps -> then, elseSteps -> else",
            "description": "Conditional uses 'then'/'else', not 'thenSteps'/'elseSteps'"
        }
    }
}
